import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Users, Trash2, Edit3, User as UserIcon } from "lucide-react";
import { Header, EmptyState } from "@/pages/Vitals";
import VoiceInput from "@/components/VoiceInput";

const empty = () => ({ name: "", relation: "Parent", age: "", gender: "", blood_group: "", address: "" });

const RELATIONS = ["Self", "Spouse", "Parent", "Child", "Sibling", "Grandparent", "Other"];
const GENDERS = ["Female", "Male", "Other", "Prefer not to say"];
const BLOOD = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"];

export default function Profiles() {
  const { members, refresh } = useMembers();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name is required"); return; }
    setLoading(true);
    const payload = { ...form, age: form.age ? parseInt(form.age) : null };
    try {
      if (editingId) await api.put(`/members/${editingId}`, payload);
      else await api.post("/members", payload);
      toast.success(editingId ? "Profile updated" : "Profile added");
      setOpen(false); setForm(empty()); setEditingId(null);
      refresh();
    } catch { toast.error("Could not save"); }
    finally { setLoading(false); }
  };

  const edit = (m) => {
    setForm({
      name: m.name, relation: m.relation || "Other", age: m.age || "",
      gender: m.gender || "", blood_group: m.blood_group || "", address: m.address || "",
    });
    setEditingId(m.id); setOpen(true);
  };

  const del = async (m) => {
    if (m.is_self) { toast.error("Self profile cannot be deleted"); return; }
    if (!window.confirm(`Delete ${m.name}'s profile?`)) return;
    await api.delete(`/members/${m.id}`);
    toast.success("Profile removed"); refresh();
  };

  return (
    <div className="space-y-6">
      <Header title="Profiles" subtitle="Manage health profiles for yourself and family members."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty()); setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-member-btn" className="rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">
                <Plus className="w-4 h-4 mr-2" /> Add Family Member
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">{editingId ? "Edit Profile" : "Add Family Member"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="member-form">
                <VoiceInput label="Name" testId="member-name" value={form.name}
                  onChange={(v) => setForm({...form, name: v})} required
                  context="Person's full name for their health profile" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84] mb-1.5">Relation</p>
                    <Select value={form.relation} onValueChange={v => setForm({...form, relation: v})}>
                      <SelectTrigger data-testid="member-relation"><SelectValue /></SelectTrigger>
                      <SelectContent>{RELATIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <VoiceInput label="Age" testId="member-age" value={form.age}
                    onChange={(v) => setForm({...form, age: v.replace(/\D/g,"")})}
                    inputMode="numeric" context="Age in years" />
                  <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84] mb-1.5">Gender</p>
                    <Select value={form.gender} onValueChange={v => setForm({...form, gender: v})}>
                      <SelectTrigger data-testid="member-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{GENDERS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84] mb-1.5">Blood Group</p>
                    <Select value={form.blood_group} onValueChange={v => setForm({...form, blood_group: v})}>
                      <SelectTrigger data-testid="member-blood"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{BLOOD.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84] mb-1.5">Address</p>
                  <Textarea data-testid="member-address" rows={2} value={form.address}
                    onChange={(e) => setForm({...form, address: e.target.value})} className="bg-[#F9F8F6] rounded-xl" />
                </div>
                <Button type="submit" disabled={loading} data-testid="save-member-btn"
                  className="w-full rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">
                  {loading ? "Saving..." : (editingId ? "Save changes" : "Add profile")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {members.length === 0 ? (
        <EmptyState icon={Users} title="No profiles yet" subtitle="Add yourself and family members to start." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map(m => (
            <Card key={m.id} className="p-5 rounded-2xl border border-[#E5E7E4]" data-testid={`profile-${m.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-[#E8ECE9] flex items-center justify-center text-[#4A6750] font-medium">
                  <UserIcon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-medium truncate">{m.name}</h3>
                    {m.is_self && <span className="text-[10px] uppercase tracking-wider bg-[#E8ECE9] text-[#4A6750] px-2 py-0.5 rounded-full font-medium">You</span>}
                  </div>
                  <p className="text-xs text-[#7D8F84] mt-0.5">{m.relation || "Other"}{m.age ? ` · ${m.age} yrs` : ""}{m.gender ? ` · ${m.gender}` : ""}</p>
                  {m.blood_group && <p className="text-xs text-[#5C6D63] mt-1">Blood: {m.blood_group}</p>}
                  {m.address && <p className="text-xs text-[#5C6D63] mt-1 line-clamp-2">{m.address}</p>}
                </div>
              </div>
              <div className="flex gap-1.5 mt-4">
                <Button size="sm" variant="ghost" onClick={() => edit(m)} data-testid={`edit-${m.id}`}
                  className="flex-1 rounded-lg"><Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
                {!m.is_self && (
                  <Button size="sm" variant="ghost" onClick={() => del(m)} data-testid={`del-${m.id}`}
                    className="rounded-lg text-[#C76B58] hover:text-[#C76B58] hover:bg-[#C76B58]/5">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
